import { create } from 'zustand'
import type { AccessAction, KsecMeProduct, KsecMeTenant } from '@/features/ksec/types'

// ─── Section key ──────────────────────────────────────────────────────────────
// Identifies the currently active dashboard section.
// Navigation between sections updates this value instead of the browser URL.

export type SectionKey =
  | 'productos'
  | 'usuarios'
  | 'roles'
  | 'plataforma_onboarding'
  | 'plataforma_orgs'
  | 'catalogo_roles'
  | 'catalogo_assets'
  | 'auditoria'

// ─── Slice shapes ─────────────────────────────────────────────────────────────

interface AuthState {
  /** Auth0 access_token (Bearer hacia ksec-svc). */
  jwt: string
  /** Auth0 id_token (SSO / deep-link a otros portales, no KSEC). */
  idToken: string
}

interface IdentityState {
  /**
   * subjectPartyPublicId — UUID del partido individual en KBRM.
   * Se usa en el header x-kbrm-party-id en TODAS las llamadas KSEC
   * y en /auth/effective-access/{partyId}.
   * Se obtiene de: GET /digital-identities/by-email/{email}
   */
  partyId: string

  /**
   * digital_identity.public_id — UUID interno de KSEC.
   * Se usa SOLO en:
   *   /digital-identities/{ksecId}
   *   /digital-identities/{ksecId}/credentials
   *   /digital-identities/{ksecId}/attachments
   * Se obtiene de: GET /digital-identities/by-email/{email}
   */
  ksecId: string

  /**
   * individual_identified — public_id del Individual en KBRM (ej: "ind_...").
   * Referencia al individuo KBRM del usuario. Usado en el endpoint
   * PATCH /create-users/organizations/:individualPublicId.
   * Se obtiene de: DigitalIdentity.individualIdentified durante bootstrap.
   */
  individualPublicId: string

  /**
   * partyRoleIdentified[0] — public_id del PartyRole del usuario en KBRM (ej: "prl_...").
   * Usado como callerPartyRolePublicId al crear nuevos usuarios en la organización
   * (granter en Permission → granter_type = 'PartyRole').
   * Se obtiene de: DigitalIdentity.partyRoleIdentified[0] durante bootstrap.
   */
  partyRolePublicId: string

  email: string
  nickname: string
  status: string
  isKashioSuper: boolean
}

interface OrgState {
  current: {
    orgPartyPublicId: string
    orgName: string | null
    /** businessTenantPublicId del primer tenant visible (KSEC v3.0). */
    businessTenantId: string | null
  } | null
  /** Tenants visibles para el usuario (v3.0 — reemplaza a visibleOrgs). */
  visibleTenants: KsecMeTenant[]
}

interface AccessState {
  map: Record<string, AccessAction>
  loaded: boolean
}

interface RolesState {
  /** involvementRole → UUID  e.g. "ACCOUNT_MANAGER" → "c000-..." */
  map: Record<string, string>
}

interface ProductsState {
  /** Productos del usuario tal como vienen en /me/context */
  items: KsecMeProduct[]
}

interface NavigationState {
  /** Active dashboard section — controls which page is rendered without changing the URL. */
  activeSection: SectionKey
}

// ─── Full store shape ─────────────────────────────────────────────────────────

interface AppStore {
  auth: AuthState
  identity: IdentityState
  org: OrgState
  access: AccessState
  roles: RolesState
  products: ProductsState
  navigation: NavigationState

  setAuth: (patch: Partial<AuthState>) => void
  setIdentity: (patch: Partial<IdentityState>) => void
  setOrg: (patch: Partial<OrgState>) => void
  setAccessMap: (map: Record<string, AccessAction>, loaded?: boolean) => void
  setRolesMap: (map: Record<string, string>) => void
  setContextProducts: (items: KsecMeProduct[]) => void
  setActiveSection: (section: SectionKey) => void
  reset: () => void
}

// ─── Initial values ───────────────────────────────────────────────────────────

const initialAuth: AuthState = { jwt: '', idToken: '' }
const initialIdentity: IdentityState = {
  partyId: '',
  ksecId: '',
  individualPublicId: '',
  partyRolePublicId: '',
  email: '',
  nickname: '',
  status: '',
  isKashioSuper: false,
}
const initialOrg: OrgState = { current: null, visibleTenants: [] }
const initialAccess: AccessState = { map: {}, loaded: false }
const initialRoles: RolesState = { map: {} }
const initialProducts: ProductsState = { items: [] }
const initialNavigation: NavigationState = { activeSection: 'productos' }

// ─── Store ────────────────────────────────────────────────────────────────────

export const useAppStore = create<AppStore>((set) => ({
  auth: initialAuth,
  identity: initialIdentity,
  org: initialOrg,
  access: initialAccess,
  roles: initialRoles,
  products: initialProducts,
  navigation: initialNavigation,

  setAuth: (patch) =>
    set((s) => ({ auth: { ...s.auth, ...patch } })),

  setIdentity: (patch) =>
    set((s) => ({ identity: { ...s.identity, ...patch } })),

  setOrg: (patch) =>
    set((s) => ({ org: { ...s.org, ...patch } })),

  setAccessMap: (map, loaded = true) =>
    set((s) => ({ access: { ...s.access, map, loaded } })),

  setRolesMap: (map) =>
    set((s) => ({ roles: { ...s.roles, map } })),

  setContextProducts: (items) =>
    set(() => ({ products: { items } })),

  setActiveSection: (section) =>
    set((s) => ({ navigation: { ...s.navigation, activeSection: section } })),

  reset: () =>
    set({
      auth: initialAuth,
      identity: initialIdentity,
      org: initialOrg,
      access: initialAccess,
      roles: initialRoles,
      products: initialProducts,
      navigation: initialNavigation,
    }),
}))
